import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, X, Check, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import PasswordInput from './PasswordInput';

/**
 * "Cambia password" trigger + modal for the user Profile.
 * Works for both email and phone users (backend decides whether current_password
 * is required based on the stored password_hash).
 */
export default function ChangePasswordButton({ hasPassword = true }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrentPw(''); setNewPw(''); setConfirmPw(''); };
  const close = () => { if (!saving) { setOpen(false); reset(); } };

  const submit = async (e) => {
    e?.preventDefault();
    if (newPw.length < 8 || !/\d/.test(newPw)) {
      toast.error(t('accountSecurity.passwordTooShort'));
      return;
    }
    if (newPw !== confirmPw) {
      toast.error(t('accountSecurity.passwordMismatch'));
      return;
    }
    setSaving(true);
    try {
      const res = await httpClient.post(`${API_URL}/api/users/me/change-password`, {
        current_password: hasPassword ? currentPw : '',
        new_password: newPw,
        confirm_password: confirmPw,
      });
      const revoked = res.data?.other_sessions_revoked || 0;
      if (revoked > 0) {
        toast.success(t('accountSecurity.passwordUpdatedAndRevoked', { n: revoked }));
      } else {
        toast.success(res.data?.message || t('accountSecurity.passwordUpdated'));
      }
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('accountSecurity.passwordChangeError'));
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving ||
    !newPw || !confirmPw ||
    (hasPassword && !currentPw);

  const title = hasPassword ? t('accountSecurity.changePassword') : t('accountSecurity.setPassword');
  const desc = hasPassword ? t('accountSecurity.changePasswordDesc') : t('accountSecurity.setPasswordDesc');

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="change-password-btn"
      >
        <Lock className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left">
          <div className="font-medium">{title}</div>
          <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {desc}
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={close}
          data-testid="change-password-overlay"
        >
          <form
            onSubmit={submit}
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {title}
              </div>
              <button
                type="button"
                onClick={close}
                className={`${isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                data-testid="change-password-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {hasPassword && (
                <PasswordInput
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  placeholder={t('accountSecurity.currentPassword')}
                  autoComplete="current-password"
                  autoFocus
                  className={`w-full px-4 h-12 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 ${
                    isDark ? 'bg-white/5 text-white border border-white/15 placeholder-white/50' : 'bg-gray-50 text-gray-900 border border-gray-200 placeholder-gray-400'
                  }`}
                  iconColor={isDark ? 'text-white/60 hover:text-white/90' : 'text-gray-400 hover:text-gray-600'}
                  data-testid="current-password-input"
                />
              )}
              <PasswordInput
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder={t('accountSecurity.newPassword')}
                autoComplete="new-password"
                autoFocus={!hasPassword}
                className={`w-full px-4 h-12 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 ${
                  isDark ? 'bg-white/5 text-white border border-white/15 placeholder-white/50' : 'bg-gray-50 text-gray-900 border border-gray-200 placeholder-gray-400'
                }`}
                iconColor={isDark ? 'text-white/60 hover:text-white/90' : 'text-gray-400 hover:text-gray-600'}
                data-testid="new-password-input-profile"
              />
              <PasswordInput
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder={t('accountSecurity.confirmPassword')}
                autoComplete="new-password"
                className={`w-full px-4 h-12 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 ${
                  isDark ? 'bg-white/5 text-white border border-white/15 placeholder-white/50' : 'bg-gray-50 text-gray-900 border border-gray-200 placeholder-gray-400'
                }`}
                iconColor={isDark ? 'text-white/60 hover:text-white/90' : 'text-gray-400 hover:text-gray-600'}
                data-testid="confirm-password-input-profile"
              />
            </div>

            <div className={`p-4 flex items-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={close}
                disabled={saving}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                data-testid="change-password-cancel"
              >
                {t('accountSecurity.cancel')}
              </button>
              <button
                type="submit"
                disabled={disabled}
                className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="change-password-submit"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Check className="w-4 h-4" /> {t('accountSecurity.save')}</>
                )}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
